/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk } from "@reduxjs/toolkit";
import { AuthenticationTypeV3 } from "@tivocorp/data-domain-lib";
import type {
  AuthConfigurationV3,
  DomainToken,
  ProvisionDevicesResponseV2
} from "@tivocorp/data-domain-lib";

import {
  buildClaimJwt,
  buildPrimaryCredentialJwt
} from "../../../model/utility";
import { Log } from "../../../model/logging";
import {
  SignInFlow,
  SignInFlowEvent,
  SignInLogger,
  SignInStep,
  type SignInLogFields,
  type SignInStepName
} from "../../../model/logging/SignInLogger";
import { SignInInteractor } from "../../../model/interactor";
import { DeviceModel } from "../../../model/device";
import { TokenManager } from "../../../model/auth";
import { RetryMechanism } from "../../../model/retrymechanism";
import { DiagnosticSeverity } from "../../../model/types";
import { IP_AUTO_LOGIN_TOTAL_TIMEOUT_MS } from "../../../config";
import type { UnityRootState, UnityAppDispatch } from "../../UnityAppStore";
import type { AuthTypeKind, IpAutoLoginError } from "../signIn.types";
import {
  AUTH_ORCHESTRATION_FAILED,
  AUTH_ORCHESTRATION_INTERACTIVE,
  AUTH_ORCHESTRATION_IP_AUTO_LOGIN,
  AUTH_ORCHESTRATION_RESOLVING,
  AUTH_TYPE_KIND_INTERACTIVE,
  AUTH_TYPE_KIND_SILENT,
  IP_AUTOLOGIN_EMPTY_CREDENTIAL_MESSAGE,
  IP_AUTO_LOGIN_STATUS_BINDING,
  IP_AUTO_LOGIN_STATUS_CLAIMING,
  IP_AUTO_LOGIN_STATUS_FETCHING_DOMAIN_TOKEN,
  IP_AUTO_LOGIN_STATUS_FETCHING_IP_JWT,
  IP_AUTO_LOGIN_STATUS_PROVISIONING,
  IPL_DEVICE_IDENTITY_RESET,
  IPL_EMPTY_CREDENTIAL,
  IPL_NO_ENDPOINT
} from "../signIn.types";
import {
  markAuthTypeAttempted,
  setAuthOrchestrationStatus,
  setIpAutoLoginStatus
} from "../signIn.actions";
import {
  beginSignInAttempt,
  classifyAuthType,
  classifyIpAutoLoginError,
  completeSignInAttempt,
  createSilentRetryOptions,
  failSignInAttempt,
  isIpAutoLoginFeatureEnabled,
  isDeviceIdentityRemovedError,
  isIpAutoLoginInFlight,
  markStep,
  markStepReached,
  persistProvisionData,
  recoverRemovedDeviceIdentity,
  resolveIpAutoLoginAuthenticationUrl,
  SIGN_IN_FALLBACK_ERROR_CODE,
  toIpAutoLoginError
} from "../common";

const TAG: string = "signIn/ipAutoLoginThunks";

/** `reason` values on auth_type_skipped diagnostics. */
const REASON_FEATURE_FLAG_DISABLED: string = "feature_flag_disabled";
const REASON_UNSUPPORTED: string = "unsupported";

/**
 * Whether this session has already re-run IP auto-login after a device
 * identity reset (DPS 410). One re-run is allowed: if the new identity is
 * rejected too, the flow falls back like any other failure, so a backend that
 * keeps answering 410 cannot loop the walk.
 */
let hasRetriedAfterIdentityReset: boolean = false;

/** Test seam — restores the once-per-session allowance. */
export function resetIpIdentityRecoveryForTest(): void {
  hasRetriedAfterIdentityReset = false;
}

/**
 * Emits auth_type_fallback when the walk lands on `authType` only because an
 * earlier type was already attempted. A run of these means the MSO's auth
 * configuration is advertising types this client cannot complete.
 */
function logAuthTypeFallback(attempted: string[], authType: string): void {
  if (attempted.length === 0) {
    return;
  }
  const fields: SignInLogFields = {
    from_type: attempted[attempted.length - 1],
    to_type: authType,
    attempted_count: String(attempted.length)
  };
  SignInLogger.logFlowEvent(
    SignInFlowEvent.AuthTypeFallback,
    "Falling back to the next auth type",
    DiagnosticSeverity.Warning,
    fields
  );
  // Also operational: diagnostics are backhaul-gated, and a fleet-wide rise in
  // fallbacks means an MSO's auth configuration needs attention.
  SignInLogger.logSignInFallback(fields);
}

/**
 * Walks the displayRank-sorted auth configurations and dispatches the
 * appropriate flow for the first unattempted entry:
 *  - "silent"      → dispatch startIpAutoLoginSignIn
 *  - "interactive" → set activeTab, status "interactive" (end walk)
 *  - "unsupported" → skip, mark attempted, continue walk
 *  - exhausted     → status "failed", set signInError
 */
export const resolveNextAuthStep = createAsyncThunk<
  void,
  void,
  { state: UnityRootState; dispatch: UnityAppDispatch }
>(
  "signIn/resolveNextAuthStep",
  async (_: void, { dispatch, getState }): Promise<void> => {
    const state: UnityRootState = getState();
    const configs: AuthConfigurationV3[] = state.signIn.authConfigurations;
    const attempted: string[] = state.signIn.attemptedAuthTypes;

    // The view re-dispatches this walk whenever the status is "resolving",
    // which includes the "resolving" the walk sets on itself. Bail out
    // before touching the status so a silent flow that is already running
    // is neither duplicated nor knocked back to "resolving".
    if (isIpAutoLoginInFlight(state.signIn.ipAutoLoginStatus)) {
      Log.d(
        TAG,
        "resolveNextAuthStep() silent flow already in flight (" +
          state.signIn.ipAutoLoginStatus +
          "), ignoring"
      );
      return;
    }

    dispatch(setAuthOrchestrationStatus(AUTH_ORCHESTRATION_RESOLVING));

    for (let i: number = 0; i < configs.length; i = i + 1) {
      const config: AuthConfigurationV3 = configs[i];
      const authType: string = config.authenticationType;

      if (attempted.indexOf(authType) !== -1) {
        continue;
      }

      const kind: AuthTypeKind = classifyAuthType(authType);

      if (kind === AUTH_TYPE_KIND_SILENT) {
        // The silent flow is behind a localStorage feature flag. With the
        // flag off, treat the entry like an unsupported one — mark it
        // attempted and continue the walk so the next authenticationType in
        // displayRank order takes over.
        if (!isIpAutoLoginFeatureEnabled()) {
          Log.i(
            TAG,
            "resolveNextAuthStep() IP auto-login disabled by feature flag, " +
              "skipping " +
              authType
          );
          SignInLogger.logFlowEvent(
            SignInFlowEvent.AuthTypeSkipped,
            "Auth type skipped: " + authType,
            DiagnosticSeverity.Info,
            {
              auth_type: authType,
              display_rank: String(config.displayRank),
              reason: REASON_FEATURE_FLAG_DISABLED
            }
          );
          dispatch(markAuthTypeAttempted(authType));
          continue;
        }

        Log.d(
          TAG,
          "resolveNextAuthStep() dispatching silent flow for " + authType
        );
        logAuthTypeFallback(attempted, authType);
        SignInLogger.logFlowEvent(
          SignInFlowEvent.AuthTypeResolved,
          "Auth type resolved: " + authType,
          DiagnosticSeverity.Info,
          {
            auth_type: authType,
            display_rank: String(config.displayRank),
            classification: AUTH_TYPE_KIND_SILENT
          }
        );
        dispatch(setAuthOrchestrationStatus(AUTH_ORCHESTRATION_IP_AUTO_LOGIN));
        dispatch(
          startIpAutoLoginSignIn({ signal: new AbortController().signal })
        );
        return;
      }

      if (kind === AUTH_TYPE_KIND_INTERACTIVE) {
        Log.d(TAG, "resolveNextAuthStep() interactive type found: " + authType);
        logAuthTypeFallback(attempted, authType);
        SignInLogger.logFlowEvent(
          SignInFlowEvent.AuthTypeResolved,
          "Auth type resolved: " + authType,
          DiagnosticSeverity.Info,
          {
            auth_type: authType,
            display_rank: String(config.displayRank),
            classification: AUTH_TYPE_KIND_INTERACTIVE
          }
        );
        dispatch(setAuthOrchestrationStatus(AUTH_ORCHESTRATION_INTERACTIVE));
        return;
      }

      // unsupported — mark attempted, log, continue walk
      Log.w(
        TAG,
        "resolveNextAuthStep() unsupported auth type: " +
          authType +
          ", skipping"
      );
      SignInLogger.logFlowEvent(
        SignInFlowEvent.AuthTypeSkipped,
        "Auth type skipped: " + authType,
        DiagnosticSeverity.Warning,
        {
          auth_type: authType,
          display_rank: String(config.displayRank),
          reason: REASON_UNSUPPORTED
        }
      );
      dispatch(markAuthTypeAttempted(authType));
    }

    // All configs exhausted
    Log.e(TAG, "resolveNextAuthStep() all auth types exhausted");
    SignInLogger.logFlowEvent(
      SignInFlowEvent.FlowFailed,
      "All auth types exhausted",
      DiagnosticSeverity.Error,
      {
        flow: SignInFlow.Orchestration,
        config_count: String(configs.length),
        attempted_count: String(attempted.length)
      }
    );
    dispatch(setAuthOrchestrationStatus(AUTH_ORCHESTRATION_FAILED));
  }
);

/**
 * The subset of an AuthConfigurationV3 that the IP auto-login sequence needs,
 * with the optional idpMetaData chain already resolved.
 */
interface IIpAutoLoginConfig {
  authenticationType: AuthConfigurationV3["authenticationType"];
  authenticationUrl: string;
  claimScheme: string;
  bindScheme: string;
  domainTokenScheme: string;
}

/**
 * Picks the tivoIpAutoLoginIdp entry out of the auth configurations and
 * flattens the fields the sequence needs, including the IDP endpoint the
 * auth-config service advertises for this MSO
 * (`idpMetaData.authenticationUrl`, falling back to the TSRI endpoints
 * catalog — see resolveIpAutoLoginAuthenticationUrl). Returns an
 * IpAutoLoginError instead when the entry — or that endpoint — is absent; both
 * cases are fallback-eligible, i.e. the caller should drop back to interactive
 * sign-in.
 */
async function resolveIpAutoLoginConfig(
  configs: AuthConfigurationV3[]
): Promise<{ config: IIpAutoLoginConfig } | { error: IpAutoLoginError }> {
  const ipConfig: AuthConfigurationV3 | undefined = configs.find(
    (c: AuthConfigurationV3): boolean =>
      c.authenticationType === AuthenticationTypeV3.TivoIpAutoLoginIdp
  );

  if (ipConfig === undefined) {
    return {
      error: {
        code: SIGN_IN_FALLBACK_ERROR_CODE,
        reason: IPL_NO_ENDPOINT,
        message: "No tivoIpAutoLoginIdp auth configuration found",
        fallbackEligible: true
      }
    };
  }

  const authenticationUrl: string | null =
    await resolveIpAutoLoginAuthenticationUrl(configs);
  if (authenticationUrl === null) {
    return {
      error: {
        code: SIGN_IN_FALLBACK_ERROR_CODE,
        reason: IPL_NO_ENDPOINT,
        message: "No IP auto-login endpoint could be resolved",
        fallbackEligible: true
      }
    };
  }

  return {
    config: {
      authenticationType: ipConfig.authenticationType,
      authenticationUrl,
      claimScheme: ipConfig.authorizationSchemes.claim,
      bindScheme: ipConfig.authorizationSchemes.bind,
      domainTokenScheme: ipConfig.authorizationSchemes.domainToken
    }
  };
}

/**
 * Emits the flow_failed diagnostic for a failed IP auto-login step.
 *
 * Only the classified error code is recorded — never the underlying service
 * message, which can echo back operator- or account-specific detail.
 */
function logIpAutoLoginFailure(
  stepReached: string,
  classified: IpAutoLoginError,
  flowStartTime: number
): void {
  SignInLogger.logFlowEvent(
    SignInFlowEvent.FlowFailed,
    "IP auto-login flow failed",
    DiagnosticSeverity.Error,
    {
      flow: SignInFlow.IpAutoLogin,
      step_reached: stepReached,
      error_code: classified.code,
      // The classifier's finer cause, kept alongside the catalog code so no
      // detail is lost while /bind and /v1/ip-autologin/token report A010.
      reason: classified.reason,
      fallback_eligible: String(classified.fallbackEligible),
      duration_ms: String(Date.now() - flowStartTime)
    }
  );
  failSignInAttempt(classified.code, stepReached as SignInStepName);
}

/**
 * IP Auto-Login Sign-In (silent, zero-interaction).
 * Steps mirror signInWithCredentials but use ipAutologinToken for step 3.
 *
 * Silent end to end: every step runs on `createSilentRetryOptions` — no
 * overlay, a short bounded retry, an immediate stop on a definitive
 * rejection — and every failure is fallback-eligible, so the walk moves on to
 * the next auth type instead of showing the user an error for a sign-in they
 * never asked for.
 *
 *   Step 2: /claim  (claimScheme + claimJwt)
 *   Step 3: ipAutologinToken (IP → JWT, INTERACTIVE retry + watchdog)
 *   Step 4: /bind   (bindScheme + primaryCredential + jwtToken)
 *   Step 5: /domainToken (domainTokenScheme + primaryCredential)
 *   Step 6: v2/provisionDevices
 */
export const startIpAutoLoginSignIn = createAsyncThunk<
  { domainToken: DomainToken; provisionResponse: ProvisionDevicesResponseV2 },
  { signal: AbortSignal },
  { state: UnityRootState; rejectValue: IpAutoLoginError }
>(
  "signIn/startIpAutoLoginSignIn",
  async (
    params: { signal: AbortSignal },
    { dispatch, getState, rejectWithValue }
  ) => {
    const flowStartTime: number = Date.now();
    try {
      Log.d(TAG, "startIpAutoLoginSignIn() starting IP auto-login");
      SignInLogger.logFlowEvent(
        SignInFlowEvent.FlowStarted,
        "IP auto-login flow started",
        DiagnosticSeverity.Info,
        { flow: SignInFlow.IpAutoLogin }
      );
      const signInInteractor: SignInInteractor = new SignInInteractor();
      const state: UnityRootState = getState();
      const configs: AuthConfigurationV3[] = state.signIn.authConfigurations;

      const resolved = await resolveIpAutoLoginConfig(configs);
      if ("error" in resolved) {
        logIpAutoLoginFailure(SignInStep.Config, resolved.error, flowStartTime);
        return rejectWithValue(resolved.error);
      }
      const ipConfig: IIpAutoLoginConfig = resolved.config;

      const lastAuthContext = await DeviceModel.getLastAuthContext();
      beginSignInAttempt({
        flow: SignInFlow.IpAutoLogin,
        authType: String(ipConfig.authenticationType),
        isFirstSignIn:
          lastAuthContext === undefined || lastAuthContext === null,
        // Silent: the orchestrator started this, not the user.
        isUserInitiated: false,
        msoName: DeviceModel.getTiVoSettings().getMsoName(),
        attemptedTypes: state.signIn.attemptedAuthTypes
      });

      const claimScheme: string = ipConfig.claimScheme;
      const bindScheme: string = ipConfig.bindScheme;
      const domainTokenScheme: string = ipConfig.domainTokenScheme;
      const msoName: string = DeviceModel.getTiVoSettings().getMsoName();

      // Step 2: Claim — self-sign the device public key with the device key
      Log.d(TAG, "startIpAutoLoginSignIn() Step 2: claiming device");
      dispatch(setIpAutoLoginStatus(IP_AUTO_LOGIN_STATUS_CLAIMING));

      markStepReached(SignInStep.Claim);
      const claimJwt: string = await buildClaimJwt();
      const claimStartedAt: number = Date.now();
      try {
        await RetryMechanism.callServiceWithRetry(
          () =>
            signInInteractor.fetchClaim({
              authorization: claimScheme + " " + claimJwt
            }),
          [],
          createSilentRetryOptions("GET"),
          params.signal
        );
        markStep(SignInStep.Claim, Date.now() - claimStartedAt);
      } catch (claimError: unknown) {
        Log.e(TAG, "startIpAutoLoginSignIn() Step 2 failed", claimError);
        const classifiedClaim: IpAutoLoginError = classifyIpAutoLoginError(
          claimError,
          IP_AUTO_LOGIN_STATUS_CLAIMING
        );
        logIpAutoLoginFailure(SignInStep.Claim, classifiedClaim, flowStartTime);
        return rejectWithValue(classifiedClaim);
      }

      // Capture DLID *after* buildClaimJwt() which calls initDlid()
      // internally — reading it before would return an empty string.
      const dlid: string = DeviceModel.getDeviceLoggingId();

      // Step 3: IP auto-login JWT token (INTERACTIVE retry + watchdog)
      Log.d(TAG, "startIpAutoLoginSignIn() Step 3: fetching IP JWT token");
      dispatch(setIpAutoLoginStatus(IP_AUTO_LOGIN_STATUS_FETCHING_IP_JWT));

      const watchdogController: AbortController = new AbortController();
      let watchdogFired: boolean = false;
      const watchdogTimerId: ReturnType<typeof setTimeout> = setTimeout(
        (): void => {
          watchdogFired = true;
          watchdogController.abort();
        },
        IP_AUTO_LOGIN_TOTAL_TIMEOUT_MS
      );

      markStepReached(SignInStep.IpAutologinToken);
      const ipTokenStartedAt: number = Date.now();
      let ipJwtToken: string;
      try {
        const tokenResponse = await RetryMechanism.callServiceWithRetry(
          () =>
            signInInteractor.ipAutologinToken(
              ipConfig.authenticationUrl,
              msoName,
              watchdogController.signal
            ),
          [],
          createSilentRetryOptions("POST"),
          watchdogController.signal
        );
        ipJwtToken = tokenResponse.jwt;
        markStep(SignInStep.IpAutologinToken, Date.now() - ipTokenStartedAt);
      } catch (step3Error: unknown) {
        clearTimeout(watchdogTimerId);
        const classified: IpAutoLoginError = classifyIpAutoLoginError(
          step3Error,
          IP_AUTO_LOGIN_STATUS_FETCHING_IP_JWT,
          watchdogFired
        );
        Log.e(
          TAG,
          "startIpAutoLoginSignIn() Step 3 failed: " + classified.code,
          step3Error
        );
        if (watchdogFired) {
          SignInLogger.logFlowEvent(
            SignInFlowEvent.IpAutologinTimeout,
            "IP auto-login total-timeout watchdog fired",
            DiagnosticSeverity.Warning,
            {
              flow: SignInFlow.IpAutoLogin,
              step_reached: SignInStep.IpAutologinToken,
              budget_ms: String(IP_AUTO_LOGIN_TOTAL_TIMEOUT_MS)
            }
          );
        }
        logIpAutoLoginFailure(
          SignInStep.IpAutologinToken,
          classified,
          flowStartTime
        );
        return rejectWithValue(classified);
      }
      clearTimeout(watchdogTimerId);

      if (typeof ipJwtToken !== "string" || ipJwtToken.length === 0) {
        const emptyCredentialError: IpAutoLoginError = {
          code: SIGN_IN_FALLBACK_ERROR_CODE,
          reason: IPL_EMPTY_CREDENTIAL,
          message: IP_AUTOLOGIN_EMPTY_CREDENTIAL_MESSAGE,
          fallbackEligible: true
        };
        logIpAutoLoginFailure(
          SignInStep.IpAutologinToken,
          emptyCredentialError,
          flowStartTime
        );
        return rejectWithValue(emptyCredentialError);
      }

      // Persist the IP JWT
      await DeviceModel.setIpJwtToken(ipJwtToken);

      // Step 4: Bind (primary credential + JWT token)
      Log.d(TAG, "startIpAutoLoginSignIn() Step 4: binding device");
      dispatch(setIpAutoLoginStatus(IP_AUTO_LOGIN_STATUS_BINDING));

      markStepReached(SignInStep.Bind);
      const primaryCredential: string = await buildPrimaryCredentialJwt();
      const bindStartedAt: number = Date.now();
      try {
        await RetryMechanism.callServiceWithRetry(
          () =>
            signInInteractor.fetchBind({
              authorization:
                bindScheme + " " + primaryCredential + " " + ipJwtToken
            }),
          [],
          createSilentRetryOptions("GET"),
          params.signal
        );
        markStep(SignInStep.Bind, Date.now() - bindStartedAt);
      } catch (bindError: unknown) {
        Log.e(TAG, "startIpAutoLoginSignIn() Step 4 failed", bindError);
        const classifiedBind: IpAutoLoginError = classifyIpAutoLoginError(
          bindError,
          IP_AUTO_LOGIN_STATUS_BINDING
        );
        logIpAutoLoginFailure(SignInStep.Bind, classifiedBind, flowStartTime);
        return rejectWithValue(classifiedBind);
      }

      // Step 5: Domain Token
      Log.d(TAG, "startIpAutoLoginSignIn() Step 5: fetching domain token");
      dispatch(
        setIpAutoLoginStatus(IP_AUTO_LOGIN_STATUS_FETCHING_DOMAIN_TOKEN)
      );

      markStepReached(SignInStep.DomainToken);
      const domainTokenStartedAt: number = Date.now();
      let domainToken: DomainToken;
      try {
        domainToken = await RetryMechanism.callServiceWithRetry(
          () =>
            signInInteractor.fetchDomainToken({
              authorization: domainTokenScheme + " " + primaryCredential
            }),
          [],
          createSilentRetryOptions("GET"),
          params.signal
        );
        markStep(SignInStep.DomainToken, Date.now() - domainTokenStartedAt);
      } catch (dtError: unknown) {
        Log.e(TAG, "startIpAutoLoginSignIn() Step 5 failed", dtError);
        const classifiedDt: IpAutoLoginError = classifyIpAutoLoginError(
          dtError,
          IP_AUTO_LOGIN_STATUS_FETCHING_DOMAIN_TOKEN
        );
        logIpAutoLoginFailure(
          SignInStep.DomainToken,
          classifiedDt,
          flowStartTime
        );
        return rejectWithValue(classifiedDt);
      }

      // Never log the token itself — it is a live bearer credential.
      Log.d(
        TAG,
        "startIpAutoLoginSignIn() Step 5 completed: domain token received, " +
          "expires_at=" +
          String(domainToken.expiration)
      );

      // Hand the token to its owner: TokenManager caches it and persists it
      // (no storage expiry; refresh is driven by the token's own expiration).
      await TokenManager.adoptToken(domainToken, null);

      // Step 6: Provision Devices (v2)
      Log.d(TAG, "startIpAutoLoginSignIn() Step 6: provisioning devices (v2)");
      dispatch(setIpAutoLoginStatus(IP_AUTO_LOGIN_STATUS_PROVISIONING));

      markStepReached(SignInStep.ProvisionDevices);
      const provisionStartedAt: number = Date.now();
      let provisionResponse: ProvisionDevicesResponseV2;
      try {
        provisionResponse = await RetryMechanism.callServiceWithRetry(
          () =>
            signInInteractor.createProvisionDevicesV2(
              {
                provisionDevicesRequestV2: {
                  authenticationType: ipConfig.authenticationType,
                  deviceLoggingId: dlid,
                  deviceConfig: {}
                }
              },
              domainToken.token
            ),
          [],
          createSilentRetryOptions("POST"),
          params.signal
        );
        markStep(SignInStep.ProvisionDevices, Date.now() - provisionStartedAt);
      } catch (provisionError: unknown) {
        Log.e(TAG, "startIpAutoLoginSignIn() Step 6 failed", provisionError);
        // Step 5 already handed a domain token to TokenManager. Without a
        // provisioned device it is not a session, so it must not outlive this
        // attempt — a later cold boot would otherwise restore it as one.
        await TokenManager.clear();
        let classifiedProvision: IpAutoLoginError = classifyIpAutoLoginError(
          provisionError,
          IP_AUTO_LOGIN_STATUS_PROVISIONING
        );
        if (isDeviceIdentityRemovedError(provisionError)) {
          const recovered: boolean = await recoverRemovedDeviceIdentity(
            SignInFlow.IpAutoLogin
          );
          if (recovered && !hasRetriedAfterIdentityReset) {
            // Re-run IP auto-login once with the new identity rather than
            // dropping the user to the credential form.
            hasRetriedAfterIdentityReset = true;
            classifiedProvision = {
              code: classifiedProvision.code,
              reason: IPL_DEVICE_IDENTITY_RESET,
              message: classifiedProvision.message,
              fallbackEligible: true
            };
          }
        }
        logIpAutoLoginFailure(
          SignInStep.ProvisionDevices,
          classifiedProvision,
          flowStartTime
        );
        return rejectWithValue(classifiedProvision);
      }

      // The provisioning payload carries bodyId / partnerId — do not log it.
      Log.d(
        TAG,
        "startIpAutoLoginSignIn() Step 6 completed: device provisioned"
      );

      await persistProvisionData(provisionResponse);

      // Persist the auth context for future token refresh
      await DeviceModel.setLastAuthContext({
        authenticationType: ipConfig.authenticationType,
        authorizationSchemes: {
          claim: claimScheme,
          bind: bindScheme,
          domainToken: domainTokenScheme
        }
      });

      // Sign-in always lands in the account context; a profile is chosen later.
      await TokenManager.setActiveContext(null);

      SignInLogger.logFlowEvent(
        SignInFlowEvent.FlowCompleted,
        "IP auto-login flow completed",
        DiagnosticSeverity.Info,
        {
          flow: SignInFlow.IpAutoLogin,
          auth_type: String(ipConfig.authenticationType),
          duration_ms: String(Date.now() - flowStartTime)
        }
      );
      completeSignInAttempt();
      return { domainToken, provisionResponse };
    } catch (error: unknown) {
      Log.e(TAG, "startIpAutoLoginSignIn()", error);
      const unclassified: IpAutoLoginError = toIpAutoLoginError(error);
      logIpAutoLoginFailure(SignInStep.Unknown, unclassified, flowStartTime);
      return rejectWithValue(unclassified);
    }
  }
);
